import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import {
  UntypedFormArray,
  UntypedFormBuilder,
  UntypedFormGroup,
} from '@angular/forms';
import {
  MAT_LEGACY_DIALOG_DATA as MAT_DIALOG_DATA,
  MatLegacyDialogRef as MatDialogRef,
} from '@angular/material/legacy-dialog';
import { MatLegacySnackBar as MatSnackBar } from '@angular/material/legacy-snack-bar';
import { Subject } from 'rxjs';
import { finalize, takeUntil } from 'rxjs/operators';

// CONFIG
import { SnackBarConfig } from '../../../shared/configs/snackbar.config';

// SERVICES
import { IContactDetails } from '../../../shared/interfaces/contact-details.interface';
import { OrderApiService } from '../../../shared/services/api/order/order-api.service';
import { SupplierContactApiService } from '../../../shared/services/api/supplier/supplier-contact-api.service';

// UTILS
import { trackById, trackByControlValue } from '../../../shared/util';

@Component({
  selector: 'lum-purchase-order-send-to-contacts',
  templateUrl: './purchase-order-send-to-contacts.component.html',
  styleUrls: ['./purchase-order-send-to-contacts.component.scss'],
})
export class PurchaseOrderSendToContactsComponent implements OnInit, OnDestroy {
  contactsForm :UntypedFormGroup;
  contacts :IContactDetails[] = [];
  loading :boolean = false;

  private onDestroy$ = new Subject();

  trackbyId = trackByControlValue;

  constructor(
    private supplierContactApiService :SupplierContactApiService,
    @Inject(MAT_DIALOG_DATA) public data :any,
    private dialogRef :MatDialogRef<PurchaseOrderSendToContactsComponent>,
    private fb :UntypedFormBuilder,
    private orderApiService :OrderApiService,
    private snackbar :MatSnackBar,
  ) {}

  ngOnInit() :void {
    this.initForm();
    this.getContactList();
  }

  ngOnDestroy() :void {
    this.onDestroy$.next(null);
    this.onDestroy$.complete();
  }

  onClose() :void {
    this.dialogRef.close();
  }

  onChangeContactEvent(event :any) :void {
    this.getContactList(event);
  }

  onSelectContactEvent(contact :IContactDetails) :void {
    const contactExists = this.contactsControl.value.find(
      (row :any) => row.id == contact.id,
    );
    if (!contactExists) {
      this.contactsControl.push(
        this.fb.group({
          id: contact.id,
          contact_name: contact.contactName,
          contact_email: contact.contactEmail,
          contact_phone: contact.contactPhone,
          selected: true,
        }),
      );
    }
  }

  onSubmit() :void {
    this.loading = true;

    this.orderApiService
      .sendPoToContacts({
        ...this.contactsForm.value,
        contacts: this.selectedContacts,
      })
      .pipe(
        takeUntil(this.onDestroy$),
        finalize(() => (this.loading = false)),
      )
      .subscribe({
        next: (response :any) => {
          this.dialogRef.close(response);
        },
        error: (err :any) => {
          const { error } = err;
          this.snackbar.open(error || 'Failed to send PO to contacts.', 'Close', {
            duration: SnackBarConfig.DEFAULT.duration,
          });
        },
      });
  }

  private initForm() :void {
    const { contacts } = this.data?.purchaseOrder?.factory || {};

    this.contactsForm = this.fb.group({
      contacts: this.fb.array(
        (contacts || []).map((contact :{ contact_details :IContactDetails }) =>
          this.fb.group({
            ...contact.contact_details,
            selected: true,
          }),
        ),
      ),
      purchaseOrderId: [this.data?.purchaseOrder?.id],
    });
  }

  private getContactList(keyword ?:string) :void {
    this.supplierContactApiService
      .get({ ...(keyword && { keyword }) })
      .pipe(takeUntil(this.onDestroy$))
      .subscribe({
        next: (contacts :IContactDetails[]) => {
          this.contacts = contacts;
        },
      });
  }

  get contactsControl() :UntypedFormArray {
    return this.contactsForm.get('contacts') as UntypedFormArray;
  }

  get selectedContacts() :any {
    return this.contactsControl.value.filter((contact :any) => contact.selected);
  }
}
